import { FormsArchiveTableStatus, FormsTableStatus } from 'app-constants';

export const isExternalMember = (form) => {
  return !(form?.isOwner || form?.isSignatory || form?.isEditor);
};

export const getFilteredDataSourceByCategory = (formCategoryFilter, dataSource) => {
  let formData = [...dataSource];
  if (formCategoryFilter !== 'All Forms') {
    switch (formCategoryFilter) {
      case 'My Forms':
        formData = formData.filter((data) => data.isOwner);
        break;
      case 'Team Forms':
        formData = formData.filter((data) => !data.isOwner && data.isTeamForm);
        break;
      case 'External Forms':
        formData = formData.filter((data) => !data.isTeamForm && !data.isOwner);
        break;
    }
  }

  return formData;
};

export const prepareFormsDataSource = (
  allFormsData,
  formCategoryFilter,
  justSignedFormProcessPublicId,
  search,
) => {
  const {
    waitingOnYou = [],
    waitingOnOthers = [],
    draftForms = [],
    finalizingForms = [],
    recentlyCompletedForms = [],
    declinedForms = [],
    voidedForms = [],
    canceledForms = [],
    allCompletedForms = [],
  } = allFormsData;

  let dataSource = [
    ...waitingOnYou.map((data) => ({
      ...data,
      key: data.formDocumentId,
      status: FormsTableStatus.WaitingOnYou,
    })),

    ...waitingOnOthers.map((data) => ({
      ...data,
      key: data.formDocumentId,
      status: FormsTableStatus.WaitingOnOthers,
    })),

    ...finalizingForms.map((data) => ({
      ...data,
      key: data.formDocumentId,
      status: FormsTableStatus.Finalizing,
    })),

    ...recentlyCompletedForms.map((data) => ({
      ...data,
      key: data.formDocumentId,
      status: FormsTableStatus.RecentlyCompleted,
    })),

    ...declinedForms.map((data) => ({
      ...data,
      key: data.formDocumentId,
      status: FormsTableStatus.Declined,
    })),

    ...draftForms.map((data) => ({
      ...data,
      key: data.formDocumentId,
      status: FormsTableStatus.Draft,
    })),

    ...voidedForms.map((data) => ({
      ...data,
      key: data.formDocumentId,
      status: FormsArchiveTableStatus.Voided,
    })),

    ...allCompletedForms.map((data) => ({
      ...data,
      key: data.formDocumentId,
      status: FormsArchiveTableStatus.Complete,
    })),

    ...canceledForms.map((data) => ({
      ...data,
      key: data.formDocumentId,
      status: FormsArchiveTableStatus.Canceled,
    })),
  ];

  dataSource = getFilteredDataSourceByCategory(formCategoryFilter, dataSource);

  const formsData: any = {
    waitingOnYouData: [],
    waitingOnOthersData: [],
    draftData: [],
    completedData: [],
    declinedData: [],
    voidedData: [],
    cancelledData: [],
    recentlyCompletedData: [],
  };

  for (const data of dataSource) {
    let keepItem = true;

    if (search) {
      const { formName, address } = data;
      const lowerCasedSearch = search.toLowerCase();
      keepItem =
        formName.toLowerCase().includes(lowerCasedSearch) ||
        address?.toLowerCase().includes(lowerCasedSearch);
    }

    if (!keepItem) continue;

    let addAtStart = false;

    if (
      justSignedFormProcessPublicId &&
      data.formProcessPublicId === justSignedFormProcessPublicId
    ) {
      if (data.status === FormsTableStatus.WaitingOnYou && (!data.IsSequential || !data.isOwner)) {
        // Signatures on backend work asynchronously, and most likely when requesting forms right after signing
        // it will return "Waiting on you" status for just completed form. Though we don't want to confuse users
        // and will show "Finalizing instead"

        const otherSignatories = ((data?.signatories as any) ?? []).filter((s) => !s.isYou);
        if (otherSignatories.some((s) => s.status !== 'Signed')) {
          data.status = FormsTableStatus.WaitingOnOthers;
        } else {
          data.status = FormsTableStatus.Finalizing;
        }
      }

      addAtStart = true;
    }

    switch (data.status) {
      case FormsTableStatus.WaitingOnYou:
        if (addAtStart) {
          formsData.waitingOnYouData.unshift(data);
        } else {
          formsData.waitingOnYouData.push(data);
        }
        break;

      case FormsTableStatus.WaitingOnOthers:
        if (addAtStart) {
          formsData.waitingOnOthersData.unshift(data);
        } else {
          formsData.waitingOnOthersData.push(data);
        }
        break;

      case FormsTableStatus.Declined:
        if (addAtStart) {
          formsData.declinedData.unshift(data);
        } else {
          formsData.declinedData.push(data);
        }
        break;

      case FormsTableStatus.Draft:
        if (addAtStart) {
          formsData.draftData.unshift(data);
        } else {
          formsData.draftData.push(data);
        }
        break;

      case FormsArchiveTableStatus.Voided:
        if (addAtStart) {
          formsData.voidedData.unshift(data);
        } else {
          formsData.voidedData.push(data);
        }
        break;

      case FormsArchiveTableStatus.Complete:
        if (addAtStart) {
          formsData.completedData.unshift(data);
        } else {
          formsData.completedData.push(data);
        }
        break;

      case FormsArchiveTableStatus.Canceled:
        if (addAtStart) {
          formsData.cancelledData.unshift(data);
        } else {
          formsData.cancelledData.push(data);
        }
        break;

      default:
        if (addAtStart) {
          formsData.recentlyCompletedData.unshift(data);
        } else {
          formsData.recentlyCompletedData.push(data);
        }
        break;
    }
  }

  return formsData;
};

export const calculatePendingSignatures = (signatories) => {
  return signatories.filter(
    ({ status }) => status === 'Requested' || status.toLowerCase().includes('waiting on'),
  ).length;
};
